import { io, Socket } from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import { Subscription } from 'rxjs';
import { Logger } from '../utils/logger';
import { AppConfig } from '../config';
import { TokenRefreshService } from '../services/tokenRefreshService';

export interface SocketConnectionConfig {
  namespace: string;
  userId: string;
}

interface JwtPayload {
  exp: number;
  user?: { id: string };
}

const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

export abstract class BaseSocketService {
  protected socket: Socket | null = null;
  protected logger: Logger;
  protected activeSubscriptions = 0;
  protected namespace: string = '';
  private tokenCheckInterval: ReturnType<typeof setInterval> | null = null;
  private hasConnectedOnce = false;
  private tokenRefreshSubscription: Subscription | null = null;

  constructor(serviceName: string) {
    this.logger = new Logger(`${serviceName}SocketService`);
    this.tokenRefreshSubscription = TokenRefreshService.onTokenUpdated().subscribe(() => {
      this.refreshConnection();
    });
  }

  private isTokenValid(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const expirationTime = decoded.exp * 1000;
      return expirationTime - Date.now() > TOKEN_EXPIRY_BUFFER_MS;
    } catch {
      return false;
    }
  }

  private getValidToken(): string | null {
    const token = localStorage.getItem(AppConfig.tokenKey);
    if (!token || !this.isTokenValid(token)) {
      return null;
    }
    return token;
  }

  private startTokenMonitoring(): void {
    if (this.tokenCheckInterval) return;

    this.tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem(AppConfig.tokenKey);
      if (!token || !this.isTokenValid(token)) {
        this.logger.debug('Token expired or invalid, disconnecting socket');
        this.disconnect();
      }
    }, 10000);
  }

  private stopTokenMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  protected async getSocket(config: SocketConnectionConfig): Promise<Socket> {
    const token = this.getValidToken();
    if (!token) {
      this.disconnect();
      throw new Error('Authentication token expired or not found');
    }

    this.namespace = config.namespace;

    if (this.socket) {
      if (this.socket.connected) {
        return this.socket;
      } else {
        this.socket.auth = { userId: config.userId, token };
        this.socket.connect();
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Socket connection timeout'));
          }, 10000);

          this.socket!.once('connect', () => {
            clearTimeout(timeout);
            resolve(this.socket!);
          });
          this.socket!.once('connect_error', (err) => {
            clearTimeout(timeout);
            reject(err);
          });
        });
      }
    }

    this.logger.debug(`Creating new socket connection to ${config.namespace}`);
    this.socket = io(`${AppConfig.socketRoot}${config.namespace}`, {
      auth: { userId: config.userId, token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5
    });

    this.socket.on('connect', () => {
      this.logger.debug('Socket connected');
      this.startTokenMonitoring();
      if (this.hasConnectedOnce) {
        this.onReconnection();
      }
      this.hasConnectedOnce = true;
    });

    this.socket.on('connect_error', (error) => {
      this.logger.error('Socket connection error:', error);
      if (error.message?.includes('Authentication') || error.message?.includes('jwt')) {
        this.disconnect();
      }
    });

    this.socket.on('disconnect', (reason) => {
      this.logger.debug(`Socket disconnected: ${reason}`);
      if (reason === 'io server disconnect') {
        const freshToken = this.getValidToken();
        if (freshToken && this.activeSubscriptions > 0) {
          this.socket?.connect();
        }
      }
    });

    this.socket.on('error', (error: Error) => {
      if (error.message?.includes('Authentication') || error.message?.includes('token')) {
        this.logger.debug('Authentication error from server, disconnecting');
        this.disconnect();
      }
    });

    this.socket.on('token-refreshed', ({ token }: { token: string }) => {
      TokenRefreshService.updateToken(token);
    });

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Socket connection timeout'));
      }, 10000);

      this.socket!.once('connect', () => {
        clearTimeout(timeout);
        resolve(this.socket!);
      });
      this.socket!.once('connect_error', (err) => {
        clearTimeout(timeout);
        reject(err);
      });
    });
  }

  protected abstract onReconnection(): void;

  protected async emitWithAuth(event: string, data: any, config: SocketConnectionConfig): Promise<void> {
    const socket = await this.getSocket(config);
    socket.emit(event, data);
  }

  public disconnect(): void {
    this.stopTokenMonitoring();
    this.tokenRefreshSubscription?.unsubscribe();
    this.tokenRefreshSubscription = null;
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.activeSubscriptions = 0;
    this.hasConnectedOnce = false;
    this.onDisconnect();
  }

  protected onDisconnect(): void {}

  public isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  public refreshConnection(): void {
    if (!this.socket || !this.namespace) return;

    const token = this.getValidToken();
    if (!token) {
      this.disconnect();
      return;
    }

    const userId = localStorage.getItem('userId') || '';
    this.socket.auth = { userId, token };

    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  protected incrementSubscriptions(): void {
    this.activeSubscriptions++;
  }

  protected decrementSubscriptions(): void {
    this.activeSubscriptions = Math.max(0, this.activeSubscriptions - 1);
  }

  public getActiveSubscriptionCount(): number {
    return this.activeSubscriptions;
  }
}
